import { SipCategoryBucketKey, SIP_ACTION_FILTER_BUCKET, SipActionFilter } from '../../sip/api/types'
import { getCategoryBucketItemCount } from '../categoryBucketsUtils'

export type CategoryWithSipBuckets = {
  categoryId?: string | null
  hasCategoryError?: boolean
  stopRecommendations?: unknown[] | null
  startRecommendations?: unknown[] | null
  increaseRecommendations?: unknown[] | null
  decreaseRecommendations?: unknown[] | null
  noActionFunds?: unknown[] | null
}

export const SIP_CATEGORY_BUCKETS: {
  bucketKey: SipCategoryBucketKey
  getFundCount: (category: CategoryWithSipBuckets | null | undefined) => number
}[] = [
  {
    bucketKey: SipCategoryBucketKey.stopRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.stopRecommendations)
  },
  {
    bucketKey: SipCategoryBucketKey.startRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.startRecommendations)
  },
  {
    bucketKey: SipCategoryBucketKey.increaseRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.increaseRecommendations)
  },
  {
    bucketKey: SipCategoryBucketKey.decreaseRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.decreaseRecommendations)
  },
  {
    bucketKey: SipCategoryBucketKey.noActionFunds,
    getFundCount: (category) => getCategoryBucketItemCount(category?.noActionFunds)
  }
]

export function buildSipCategoryBucketCompositeKey({
  categoryId,
  bucketKey
}: {
  categoryId: string
  bucketKey: SipCategoryBucketKey
}): string {
  return `${categoryId}:${bucketKey}`
}

export function hasVisibleSipPlanListContent(
  categories: CategoryWithSipBuckets[] | null | undefined,
  actionFilter: SipActionFilter
): boolean {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return false
  }

  return categories.some((category) => {
    if (category?.hasCategoryError && actionFilter === SipActionFilter.ALL) {
      return true
    }
    return getVisibleSipCategoryBucketKeys(category, actionFilter).length > 0
  })
}

export function isSipYourPlanListFullyEmpty(categories: CategoryWithSipBuckets[] | null | undefined): boolean {
  return !hasVisibleSipPlanListContent(categories, SipActionFilter.ALL)
}

type IdealCategoryWithFunds = {
  idealSips?: unknown[] | null
  hasCategoryError?: boolean
}

export function isSipIdealPlansListFullyEmpty(
  categories: IdealCategoryWithFunds[] | null | undefined
): boolean {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return true
  }
  return !categories.some(
    (category) =>
      category?.hasCategoryError === true ||
      getCategoryBucketItemCount(category?.idealSips) > 0
  )
}

export function getVisibleSipCategoryBucketKeys(
  category: CategoryWithSipBuckets | null | undefined,
  actionFilter: SipActionFilter
): SipCategoryBucketKey[] {
  if (!category) {
    return []
  }

  return SIP_CATEGORY_BUCKETS.filter(({ bucketKey, getFundCount }) => {
    if (getFundCount(category) <= 0) {
      return false
    }
    if (actionFilter === SipActionFilter.ALL) {
      return true
    }
    return bucketKey === SIP_ACTION_FILTER_BUCKET[actionFilter]
  }).map(({ bucketKey }) => bucketKey)
}

/** Display label for SIP reason copy — appends ` SIPs` to BE `categoryName`. */
export function formatSipCategorySipsLabel(categoryName?: string | null): string {
  const label = (categoryName ?? '').trim()
  if (label.length === 0) {
    return 'this category SIPs'
  }
  return `${label} SIPs`
}
